import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { usersActions } from './actions';
import { catchError, delay, map, of, switchMap, withLatestFrom } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { UsersService } from './services/users.service';
import { AlertController, NavController } from '@ionic/angular/standalone';
import { EAppRoutes } from '../../enums/app-routes.enum';
import { Store } from '@ngrx/store';
import { alertActions } from 'src/app/features/alert/store/actions';
import { toastActions } from 'src/app/features/toast/store/actions';
import { TranslateService } from '@ngx-translate/core';
import { selectAliasView } from '../employees/reducer';
import { employeeActions } from '../employees/actions';
import { isEmployee } from '../../utils/util.functions';

@Injectable()
export class UsersFeatureEffect {
  fetchUsersEffect$ = createEffect(
    (
      actions$ = inject(Actions),
      logger = inject(NGXLogger),
      usersService = inject(UsersService),
      store = inject(Store)
    ) => {
      return actions$.pipe(
        ofType(usersActions.fetchUsers),
        switchMap((action) => {
          return usersService.getUsers().pipe(
            delay(200),
            map((users) => {
              logger.info('Fetch users successful');
              return usersActions.fetchUsersSuccess({ users });
            }),
            catchError((error) => {
              store.dispatch(alertActions.showError({ error: error }));
              return of(usersActions.fetchUsersFailure());
            })
          );
        })
      );
    }
  );

  fetchUsersFailedEffect$ = createEffect(
    (
      actions$ = inject(Actions),
      logger = inject(NGXLogger),
      navCtrl = inject(NavController)
    ) => {
      return actions$.pipe(
        ofType(usersActions.fetchUsersFailure),
        map(() => {
          logger.info('Fetch users failed');
          navCtrl.navigateBack([EAppRoutes.HOME], { animated: false });
        })
      );
    },
    { dispatch: false }
  );

  addUserEffect$ = createEffect(
    (
      actions$ = inject(Actions),
      logger = inject(NGXLogger),
      usersService = inject(UsersService),
      store = inject(Store)
    ) => {
      return actions$.pipe(
        ofType(usersActions.addUser),
        switchMap((action) => {
          return usersService.createUser(action.user).pipe(
            map((user) => {
              logger.info('Add user successful');
              return usersActions.addUserSuccess({ user });
            }),
            catchError((error) => {
              store.dispatch(alertActions.showError({ error: error }));
              return of(usersActions.addUserFailure());
            })
          );
        })
      );
    }
  );

  addUserSuccessEffect$ = createEffect(
    (
      actions$ = inject(Actions),
      translateService = inject(TranslateService),
      navCtrl = inject(NavController),
      alertController = inject(AlertController),
      store = inject(Store)
    ) => {
      return actions$.pipe(
        ofType(usersActions.addUserSuccess),
        switchMap(async (action) => {
          const user = action.user;

          if (!isEmployee({ ...user, role: Number(user.role) })) {
            navCtrl.navigateBack([EAppRoutes.USERS], { animated: false });
            return toastActions.showToast({
              message: translateService.instant('CORE.MESSAGES.USER_ADDED'),
            });
          }

          // Reload the employee list. `addUserSuccess` only appends to the users
          // state, but staff pages (work time regulation, time accounts, team
          // calendar) read the employees state, which is fetched separately from
          // `employee-service`. Without this the new employee is missing there
          // until the home page happens to be visited, since only
          // FlowControlService.homePage() dispatches fetchEmployees().
          // The backend already drops its cache on create (UserController ->
          // resetEmployeeData), so it expects the client to re-read.
          store.dispatch(employeeActions.fetchEmployees());

          const userName = `${user.firstName} ${user.lastName}`;
          const message = translateService.instant(
            'CORE.MESSAGES.USER_ADDED_WITH_PROMPT',
            { name: userName }
          );

          const alert = await alertController.create({
            header: translateService.instant('CORE.MESSAGES.INFORMATION'),
            message: message,
            backdropDismiss: false,
            cssClass: ['alert-app-messages', 'alert-info-message'],
            buttons: [
              {
                text: translateService.instant(
                  'CORE.MESSAGES.ADD_WORK_REGULATION'
                ),
                role: 'confirm',
                cssClass: 'alert-button-primary',
                handler: async () => {
                  await navCtrl.navigateBack([EAppRoutes.USERS], {
                    animated: false,
                  });
                  await navCtrl.navigateForward([
                    EAppRoutes.NEW_WORKTIMESCHEME,
                  ]);
                  store.dispatch(
                    employeeActions.setCurrentEmployee({
                      payload: user.id,
                    })
                  );
                },
              },
              {
                text: translateService.instant('CORE.MESSAGES.LATER'),
                role: 'cancel',
                cssClass: 'alert-button-secondary',
                handler: () => {
                  navCtrl.navigateBack([EAppRoutes.USERS], {
                    animated: false,
                  });
                },
              },
            ],
          });

          await alert.present();
          return alertActions.closeAlert();
        })
      );
    }
  );

  updateUserEffect$ = createEffect(
    (
      actions$ = inject(Actions),
      logger = inject(NGXLogger),
      usersService = inject(UsersService),
      store = inject(Store)
    ) => {
      return actions$.pipe(
        ofType(usersActions.updateUser),
        switchMap((action) => {
          return usersService.updateUser(action.id, action.user).pipe(
            map((user) => {
              logger.info('Update user successful');
              return usersActions.updateUserSuccess({ user });
            }),
            catchError((error) => {
              store.dispatch(alertActions.showError({ error: error }));
              return of(usersActions.updateUserFailure());
            })
          );
        })
      );
    }
  );

  updateUserSuccessEffect$ = createEffect(
    (
      actions$ = inject(Actions),
      translateService = inject(TranslateService),
      navCtrl = inject(NavController),
      store = inject(Store)
    ) => {
      return actions$.pipe(
        ofType(usersActions.updateUserSuccess),
        withLatestFrom(store.select(selectAliasView)),
        map(([, aliasView]) => {
          navCtrl.navigateBack([EAppRoutes.USER], { animated: false });
          if (aliasView) {
            store.dispatch(employeeActions.fetchEmployees());
          }
          return toastActions.showToast({
            message: translateService.instant(
              'CORE.MESSAGES.USER_DATA_UPDATED'
            ),
          });
        })
      );
    }
  );

  deleteUserEffect$ = createEffect(
    (
      actions$ = inject(Actions),
      logger = inject(NGXLogger),
      usersService = inject(UsersService),
      store = inject(Store)
    ) => {
      return actions$.pipe(
        ofType(usersActions.deleteUser),
        switchMap((action) => {
          return usersService.deleteUser(action.id).pipe(
            map(() => {
              logger.info('Delete user successful');
              return usersActions.deleteUserSuccess({ id: action.id });
            }),
            catchError((error) => {
              store.dispatch(alertActions.showError({ error: error }));
              return of(usersActions.deleteUserFailure());
            })
          );
        })
      );
    }
  );

  deleteUserSuccessEffect$ = createEffect(
    (
      actions$ = inject(Actions),
      translateService = inject(TranslateService),
      navCtrl = inject(NavController)
    ) => {
      return actions$.pipe(
        ofType(usersActions.deleteUserSuccess),
        map(() => {
          navCtrl.navigateBack([EAppRoutes.USERS], { animated: false });
          return toastActions.showToast({
            message: translateService.instant('CORE.MESSAGES.USER_DELETED'),
          });
        })
      );
    }
  );
}
